/**
 * @voltxampere/schema — ComponentInstance
 *
 * Per SPECS §1.3. A component instance references a component type name
 * (validated against the registered component library at parse time via
 * cross-circuit invariants) and carries its placement, parameters, and
 * terminal-to-net connections.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
import { PointSchema } from "./_primitives.js";
/**
 * A single parameter value. SPECS §1.3 allows scalar numbers, strings,
 * booleans, or value+unit pairs like `{ value: 4.7, unit: "kΩ" }`.
 */
export const ParamValueSchema = z.union([
    z.number().finite(),
    z.string(),
    z.boolean(),
    z
        .object({
        value: z.number().finite(),
        unit: z.string().max(20),
    })
        .strict(),
]);
/**
 * A terminal's connection to a net, including the terminal's anchor
 * position on the component body. Used for hit-testing and wire routing.
 */
export const TerminalConnectionSchema = z
    .object({
    terminalId: z.string(),
    netId: z.string(),
    position: PointSchema,
})
    .strict();
/**
 * The canonical rotations supported by a component placement on the
 * canvas, per SPECS §1.3.
 */
export const RotationSchema = z.union([
    z.literal(0),
    z.literal(90),
    z.literal(180),
    z.literal(270),
]);
export const ComponentInstanceSchema = z
    .object({
    id: z.string(),
    type: z.string().regex(/^[a-z][a-z0-9-]*$/),
    position: PointSchema,
    rotation: RotationSchema,
    flipped: z.boolean().default(false),
    label: z.string().optional(),
    params: z.record(ParamValueSchema),
    terminals: z.array(TerminalConnectionSchema),
    modelRef: z.string().optional(),
})
    .strict();
//# sourceMappingURL=component.js.map